import type { EventInvoiceAttendee } from "./eventInvoiceCreateState";

export type EventAttendeeSuggestion = Readonly<{
  email: string;
  id: string;
  name: string;
  phone: string;
  /** "Primary Contact" tag (design AttendeeNameCombo). */
  role?: string | null;
}>;

/**
 * Design AttendeeNameCombo (CMP Dashboard (21) :78) and CLAUDE.md
 * "Searchable dropdown matching": with no typed text the whole list shows;
 * typed text keeps options whose label starts with it.
 */
export const filterEventAttendeeComboOptions = <Option,>({
  getLabel,
  options,
  text,
}: {
  getLabel: (option: Option) => string;
  options: readonly Option[];
  text: string;
}): Option[] => {
  const query = text.trim().toLocaleLowerCase();
  return query
    ? options.filter((option) => getLabel(option).toLocaleLowerCase().startsWith(query))
    : [...options];
};

/** Server suggestions (all events and profile contacts) first, then this
 * page's attendees, one entry per email (or name when there is no email). */
export const mergeEventAttendeeSuggestions = (
  ...lists: ReadonlyArray<readonly EventAttendeeSuggestion[]>
): EventAttendeeSuggestion[] => {
  const seen = new Set<string>();
  return lists.flat().filter((suggestion) => {
    const key = suggestion.email.trim().toLocaleLowerCase() ||
      `name:${suggestion.name.trim().toLocaleLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

type EventAttendeeSource = Readonly<{
  email?: string | null;
  firstName?: string | null;
  id?: string | null;
  lastName?: string | null;
  phone?: string | null;
}>;

type EventRegistrationAttendeeSource = Readonly<{
  attendees?: readonly EventAttendeeSource[] | null;
  chamberId?: string | null;
  eventId?: string | null;
  selectedMemberCompanyId?: string | null;
}>;

type EventSponsorshipAttendeeSource = Readonly<{
  chamberId?: string | null;
  eventId?: string | null;
  includedAttendees?: readonly EventAttendeeSource[] | null;
  selectedMemberCompanyId?: string | null;
}>;

const normalizeIdentity = (value?: string | null): string =>
  value?.trim() || "";

const normalizeAttendeeName = (attendee: EventAttendeeSource): string =>
  [attendee.firstName?.trim(), attendee.lastName?.trim()]
    .filter(Boolean)
    .join(" ");

const getSuggestionIdentity = ({
  email,
  name,
  phone,
}: Pick<EventAttendeeSuggestion, "email" | "name" | "phone">): string =>
  [name.toLocaleLowerCase(), email.toLocaleLowerCase(), phone].join("\u0000");

export const getEventAttendeeSuggestions = ({
  chamberId,
  eventId,
  registrations = [],
  selectedMemberCompanyId,
  sponsorships = [],
}: {
  chamberId: string;
  eventId: string;
  registrations?: readonly EventRegistrationAttendeeSource[];
  selectedMemberCompanyId: string;
  sponsorships?: readonly EventSponsorshipAttendeeSource[];
}): EventAttendeeSuggestion[] => {
  const normalizedChamberId = normalizeIdentity(chamberId);
  const normalizedEventId = normalizeIdentity(eventId);
  const normalizedMemberId = normalizeIdentity(selectedMemberCompanyId);

  if (!normalizedChamberId || !normalizedEventId || !normalizedMemberId) {
    return [];
  }

  const attendeeSources: EventAttendeeSource[] = [];
  const sourceMatchesScope = (source: {
    chamberId?: string | null;
    eventId?: string | null;
    selectedMemberCompanyId?: string | null;
  }): boolean =>
    normalizeIdentity(source.chamberId) === normalizedChamberId &&
    normalizeIdentity(source.eventId) === normalizedEventId &&
    normalizeIdentity(source.selectedMemberCompanyId) === normalizedMemberId;

  registrations.forEach((registration) => {
    if (sourceMatchesScope(registration)) {
      attendeeSources.push(...(registration.attendees || []));
    }
  });
  sponsorships.forEach((sponsorship) => {
    if (sourceMatchesScope(sponsorship)) {
      attendeeSources.push(...(sponsorship.includedAttendees || []));
    }
  });

  const seen = new Set<string>();

  return attendeeSources.flatMap((attendee, index) => {
    const name = normalizeAttendeeName(attendee);

    if (!name) {
      return [];
    }

    const suggestion = {
      email: normalizeIdentity(attendee.email),
      id:
        normalizeIdentity(attendee.id) ||
        `event-attendee-suggestion-${index + 1}`,
      name,
      phone: normalizeIdentity(attendee.phone),
    };
    const identity = getSuggestionIdentity(suggestion);

    if (seen.has(identity)) {
      return [];
    }

    seen.add(identity);
    return [suggestion];
  });
};

export const applyEventAttendeeSuggestion = ({
  attendee,
  suggestion,
}: {
  attendee: EventInvoiceAttendee;
  suggestion: EventAttendeeSuggestion;
}): EventInvoiceAttendee => ({
  ...attendee,
  email: suggestion.email,
  name: suggestion.name,
  phone: suggestion.phone,
});
